import { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { api } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { Card, Input, Select, Spinner, Button } from '../../components/shared/StyledComponents'
import { OpportunitySummaryModal } from '../Timeline/components/OpportunitySummaryModal'
import { openOpportunity } from '../../utils/openOpportunity'

// Format status labels to be human-readable
const formatStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
        ganada: 'Ganada',
        perdida: 'Perdida',
        cancelada: 'Cancelada',
        en_curso: 'En Curso',
        'en curso': 'En Curso',
        en_negociacion: 'En Negociación',
        presentada: 'Presentada',
        en_preparacion: 'En Preparación',
        en_revision: 'En Revisión',
        borrador: 'Borrador',
        activo: 'Activo',
        pendiente: 'Pendiente',
        prospecto: 'Prospecto',
        planificacion: 'Planificación',
        finalizado: 'Finalizado',
        completado: 'Completado',
        cerrado: 'Cerrado',
        no_iniciado: 'No Iniciado',
        en_espera: 'En Espera',
        aprobada: 'Aprobada',
        rechazada: 'Rechazada',
        incompleta: 'Incompleta',
    }
    return statusMap[status?.toLowerCase()] || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Sin estado'
}

const PageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 100%;
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`

const PageTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`

const FiltersCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 500;
`

const SummaryCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  justify-content: center;
`

const SummaryItem = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.md};
`

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`

const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow-x: auto;
  overflow-y: auto;
  padding: 0;
  max-height: 70vh;
  position: relative;
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.85rem;
`

const TableHeader = styled.thead`
  background: ${({ theme }) => theme.colors.primary};
  color: white;

  th {
    position: sticky;
    top: 0;
    z-index: 20;
  }
`

const HeaderCell = styled.th`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: left;
  font-weight: 600;
  font-size: 0.85rem;
  white-space: nowrap;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  background: ${({ theme }) => theme.colors.primary};

  &:last-child {
    border-right: none;
  }

  &:first-child {
    position: sticky;
    left: 0;
    top: 0;
    z-index: 30;
    background: ${({ theme }) => theme.colors.primary};
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
  }
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
`

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing.md};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
  background: white;

  &:last-child {
    border-right: none;
  }

  &:first-child {
    position: sticky;
    left: 0;
    z-index: 10;
    background: white;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }

  tr:nth-child(even) & {
    background: ${({ theme }) => theme.colors.background};
  }

  tr:nth-child(even) &:first-child {
    background: ${({ theme }) => theme.colors.background};
  }

  tr:hover & {
    background: #e8f0fe;
    cursor: pointer;
  }

  tr:hover &:first-child {
    background: #e8f0fe;
  }
`

const OpportunityName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.95rem;
  margin-bottom: 4px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: underline;
  }
`

const OpportunityClient = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ status }) => {
        switch (status?.toLowerCase()) {
            case 'ganada': return '#2E7D32'
            case 'en_curso': case 'en curso': case 'activo': return '#1565C0'
            case 'perdida': case 'cancelada': return '#C62828'
            case 'en_negociacion': case 'pendiente': return '#FF9800'
            case 'presentada': case 'prospecto': return '#7B1FA2'
            case 'en_preparacion': case 'en_revision': return '#0288D1'
            case 'incompleta': return '#FF5722'
            case 'borrador': return '#757575'
            default: return '#9E9E9E'
        }
    }};
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`

const DateCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const DateLabel = styled.span`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
`

const DateValue = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
`

const TeamList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const TeamMember = styled.span<{ load?: number }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  background: ${({ load }) => {
        if (!load || load === 0) return '#f5f5f5'
        if (load > 50) return '#ffebee'
        if (load > 25) return '#fff3e0'
        return '#e8f5e9'
    }};
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
`

const LoadBadge = styled.span<{ load: number }>`
  font-weight: 700;
  color: ${({ load }) => {
        if (load > 50) return '#f44336'
        if (load > 25) return '#ff9800'
        return '#4caf50'
    }};
`

const AmountCell = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.success};
`

const ProbabilityCell = styled.div<{ value: number }>`
  font-weight: 600;
  color: ${({ value }) => {
        if (value >= 70) return '#2e7d32'
        if (value >= 40) return '#f57c00'
        return '#c62828'
    }};
`

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
`

const NoData = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textMuted};
`

const Legend = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const LegendColor = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: ${({ color }) => color};
`

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const NoTeam = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
  font-size: 0.75rem;
`

const ExportButton = styled(Button)`
  white-space: nowrap;
`

interface TeamAssignment {
    userId: string
    userName: string
    load: number
}

interface Opportunity {
    id: string
    name: string
    client: string
    status: string
    sector?: string
    technology?: string
    technologies?: string[]
    tipoProyecto?: string
    probability?: number
    amount?: number
    oferta_code?: string
    oferta: {
        estado: string
        fecha_inicio?: string
        fecha_entrega?: string
        equipo?: TeamAssignment[]
    }
    proyecto: {
        codigo?: string
        estado?: string
        fecha_inicio?: string
        fecha_fin?: string
        equipo?: TeamAssignment[]
    }
}

interface Categories {
    tecnologias: Record<string, string[]>
    sectores: string[]
}

const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '-'
    try {
        return new Date(dateStr).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'short',
            year: '2-digit'
        })
    } catch {
        return '-'
    }
}

const formatAmount = (amount?: number | null): string => {
    if (!amount) return '-'
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M€`
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K€`
    return `${amount}€`
}

export const MatrizOportunidadesPage: React.FC = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const isAdmin = user?.role?.toLowerCase() === 'admin'
    const [opportunities, setOpportunities] = useState<Opportunity[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [sectorFilter, setSectorFilter] = useState('')
    const [techFilter, setTechFilter] = useState('')
    const [tipoProyectoFilter, setTipoProyectoFilter] = useState('')
    const [sectors, setSectors] = useState<string[]>([])
    const [techCategories, setTechCategories] = useState<string[]>([])
    const [refreshKey, setRefreshKey] = useState(0)
    const [summaryModalOppId, setSummaryModalOppId] = useState<number | null>(null)
    const [excludeLostCancelled, setExcludeLostCancelled] = useState(true)

    // Force refresh on every mount (navigation to this page)
    useEffect(() => {
        setRefreshKey(prev => prev + 1)
    }, [])

    useEffect(() => {
        loadData()
    }, [refreshKey])

    const loadData = async () => {
        try {
            setLoading(true)
            const [opportunitiesRes, configRes, usersRes] = await Promise.all([
                api.opportunities.getAll(),
                api.config.getCategories(),
                api.users.getAll(),
            ])

            // Build users map for names
            const usersData = Array.isArray(usersRes.data) ? usersRes.data : []
            const usersMap = new Map(usersData.map((u: any) => [String(u.id), u.display_name || u.username || 'Usuario']))

            // Map opportunities data
            const oppsData = Array.isArray(opportunitiesRes.data) ? opportunitiesRes.data : []
            const mappedOpps: Opportunity[] = oppsData.map((opp: any) => {
                // Map equipo with user names
                const mapTeam = (team?: any[]): TeamAssignment[] => {
                    if (!team || !Array.isArray(team)) return []
                    return team.map((t: any) => ({
                        userId: String(t.userId || t.user_id || t.id),
                        userName: t.userName || t.display_name || usersMap.get(String(t.userId || t.user_id || t.id)) || 'Usuario',
                        load: t.load || t.capacity_pct || t.dedication || 0,
                    }))
                }

                // IMPORTANT: Backend returns SEPARATE teams for oferta and proyecto
                // oferta.equipo = people assigned to work on the offer/proposal
                // proyecto.equipo = people assigned to execute the project when won
                // These are independent - the same person CAN be in both with different loads
                // Do NOT use the merged 'team' field which combines both
                const ofertaEquipo = mapTeam(opp.oferta?.equipo)
                const proyectoEquipo = mapTeam(opp.proyecto?.equipo)

                return {
                    id: String(opp.id),
                    name: opp.general?.name || opp.name || 'Sin nombre',
                    client: opp.general?.gestor || opp.client || '-',
                    status: opp.oferta?.estado || opp.status || 'Sin estado',
                    sector: opp.general?.sector || '',
                    technology: opp.general?.tecnologia || '',
                    technologies: opp.general?.tecnologias || opp.tecnologias || [],
                    tipoProyecto: opp.general?.proyecto_interno ? 'Proyecto interno' : (opp.general?.tipo_proyecto || ''),
                    probability: opp.oferta?.probabilidad,
                    // Venta is what the modal and the SharePoint import write; amount is
                    // the deprecated legacy field (offers with only a Venta showed '-').
                    amount: opp.oferta?.venta ?? opp.oferta?.amount,
                    oferta_code: opp.oferta?.code || '',
                    oferta: {
                        estado: opp.oferta?.estado || 'Sin estado',
                        fecha_inicio: opp.oferta?.fecha_inicio,
                        fecha_entrega: opp.oferta?.fecha_entrega,
                        equipo: ofertaEquipo,
                    },
                    proyecto: {
                        codigo: opp.proyecto?.codigo || '',
                        estado: opp.proyecto?.estado,
                        fecha_inicio: opp.proyecto?.fecha_inicio,
                        fecha_fin: opp.proyecto?.fecha_fin,
                        equipo: proyectoEquipo,
                    },
                }
            })
            // Filter out Prácticas opportunity from display
            const filteredMappedOpps = mappedOpps.filter(o => o.name.toLowerCase() !== 'desarrollo y formación')
            setOpportunities(filteredMappedOpps)

            // Set sectors and tech categories from config
            const categories: Categories = configRes.data || { tecnologias: {}, sectores: [] }
            setSectors(categories.sectores || [])
            setTechCategories(Object.keys(categories.tecnologias || {}))
        } catch (error) {
            console.error('Error loading data:', error)
        } finally {
            setLoading(false)
        }
    }

    const statuses = useMemo(() => {
        return [...new Set(opportunities.map((o) => o.status))].filter(Boolean)
    }, [opportunities])

    const filteredOpportunities = useMemo(() => {
        return opportunities.filter((o) => {
            const matchesSearch = !searchTerm ||
                o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                o.client.toLowerCase().includes(searchTerm.toLowerCase())
            const matchesStatus = !statusFilter || o.status === statusFilter
            const matchesSector = !sectorFilter || o.sector === sectorFilter
            const matchesTech = !techFilter || (o.technologies && o.technologies.includes(techFilter)) || o.technology === techFilter
            const matchesTipo = !tipoProyectoFilter || o.tipoProyecto === tipoProyectoFilter
            const matchesExclude = !excludeLostCancelled || !['perdida', 'cancelada'].includes(o.status?.toLowerCase())
            return matchesSearch && matchesStatus && matchesSector && matchesTech && matchesTipo && matchesExclude
        })
    }, [opportunities, searchTerm, statusFilter, sectorFilter, techFilter, tipoProyectoFilter, excludeLostCancelled])

    // Calculate stats
    // Convert probability text to percentage for calculations
    const probabilityToPercent = (prob: any): number => {
        if (!prob) return 0
        if (typeof prob === 'number') return prob
        const probStr = String(prob).toLowerCase()
        const mapping: Record<string, number> = {
            'baja': 25,
            'media': 50,
            'alta': 75,
            'muy alta': 90,
        }
        return mapping[probStr] || 0
    }

    // Format probability for display - uppercase text labels
    const formatProbability = (prob: any): string => {
        if (!prob) return '-'
        const probStr = String(prob).toLowerCase()
        const mapping: Record<string, string> = {
            'baja': 'BAJA',
            'media': 'MEDIA',
            'alta': 'ALTA',
            'muy alta': 'MUY ALTA',
        }
        // If it's a text value, return uppercase label
        if (mapping[probStr]) return mapping[probStr]
        // If it's a number, return with % symbol
        if (typeof prob === 'number') return `${prob}%`
        return String(prob).toUpperCase()
    }

    const totalOportunidades = filteredOpportunities.length
    const totalWithTeam = filteredOpportunities.filter(o =>
        (o.oferta.equipo && o.oferta.equipo.length > 0) ||
        (o.proyecto.equipo && o.proyecto.equipo.length > 0)
    ).length
    const totalAmount = filteredOpportunities
        .reduce((sum, o) => sum + (o.amount || 0), 0)

    // Calculate average probability using the mapping function
    const avgProbability = filteredOpportunities.length > 0
        ? Math.round(filteredOpportunities.reduce((sum, o) => sum + probabilityToPercent(o.probability), 0) / filteredOpportunities.length)
        : 0

    // Admins open the clicked opportunity's editor (the list used to open without
    // it); everyone else gets the read-only summary instead of being sent to the
    // admin-only /oportunidades, which bounced them to /profile.
    const handleRowClick = (oppId: string) => {
        openOpportunity(navigate, oppId, { isAdmin, onReadOnly: setSummaryModalOppId })
    }

    const handleDownloadOffer = async (e: React.MouseEvent, oppId: string, oppName: string) => {
        e.stopPropagation() // Prevent row click navigation
        try {
            // First, get the files for this opportunity
            const filesRes = await api.opportunities.getFiles(parseInt(oppId))
            const files = filesRes.data || []

            if (files.length === 0) {
                alert('Esta oportunidad no tiene documento de oferta adjunto')
                return
            }

            // Download the most recent file (first one)
            const file = files[0]
            const response = await api.opportunities.downloadFile(file.id)

            // Create download link with opportunity name format
            const blob = new Blob([response.data], { type: file.file_type || 'application/octet-stream' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            // Get file extension from original filename or default to pdf
            const ext = file.filename?.split('.').pop() || 'pdf'
            link.download = `${oppName}_Oferta.${ext}`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Error downloading offer file:', error)
            alert('Error al descargar el documento de oferta')
        }
    }

    const handleExportExcel = () => {
        const rows = filteredOpportunities.map((opp) => {
            const base: Record<string, string | number> = {
                'Oportunidad': opp.name,
                'Cliente': opp.client,
                'Estado': formatStatusLabel(opp.status),
                'Sector': opp.sector || '-',
                'Tecnología': opp.technologies?.join(', ') || opp.technology || '-',
                'Importe': opp.amount || 0,
                'Probabilidad': opp.probability !== undefined && opp.probability !== null
                    ? formatProbability(opp.probability)
                    : '-',
                'Cód. Oferta': opp.oferta_code || '-',
                'Oferta Inicio': opp.oferta.fecha_inicio || '-',
                'Oferta Entrega': opp.oferta.fecha_entrega || '-',
                'Equipo Oferta': opp.oferta.equipo?.map((m) => `${m.userName} (${m.load}%)`).join(', ') || '-',
                'Cód. Proyecto': opp.proyecto.codigo || '-',
                'Proyecto Inicio': opp.proyecto.fecha_inicio || '-',
                'Proyecto Fin': opp.proyecto.fecha_fin || '-',
                'Equipo Proyecto': opp.proyecto.equipo?.map((m) => `${m.userName} (${m.load}%)`).join(', ') || '-',
            }
            return base
        })

        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Matriz Oportunidades')
        XLSX.writeFile(wb, `Matriz_Oportunidades_${new Date().toISOString().slice(0, 10)}.xlsx`)
    }

    if (loading) {
        return (
            <PageContainer>
                <PageTitle>🎯 Matriz de Oportunidades</PageTitle>
                <LoadingContainer>
                    <Spinner />
                </LoadingContainer>
            </PageContainer>
        )
    }

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>🎯 Matriz de Oportunidades</PageTitle>
                {isAdmin && (
                    <ExportButton variant="secondary" size="sm" onClick={handleExportExcel}>
                        📊 Exportar Excel
                    </ExportButton>
                )}
            </PageHeader>

            <FiltersCard>
                <FiltersGrid>
                    <FilterGroup>
                        <FilterLabel>Buscar oportunidad</FilterLabel>
                        <Input
                            type="text"
                            placeholder="Nombre o cliente..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>Estado</FilterLabel>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {statuses.map((status) => (
                                <option key={status} value={status}>
                                    {formatStatusLabel(status)}
                                </option>
                            ))}
                        </Select>
                    </FilterGroup>
                    {sectors.length > 0 && (
                        <FilterGroup>
                            <FilterLabel>Sector</FilterLabel>
                            <Select
                                value={sectorFilter}
                                onChange={(e) => setSectorFilter(e.target.value)}
                            >
                                <option value="">Todos</option>
                                {sectors.map((sector) => (
                                    <option key={sector} value={sector}>
                                        {sector}
                                    </option>
                                ))}
                            </Select>
                        </FilterGroup>
                    )}
                    {techCategories.length > 0 && (
                        <FilterGroup>
                            <FilterLabel>Tecnología</FilterLabel>
                            <Select
                                value={techFilter}
                                onChange={(e) => setTechFilter(e.target.value)}
                            >
                                <option value="">Todas</option>
                                {techCategories.map((tech) => (
                                    <option key={tech} value={tech}>
                                        {tech}
                                    </option>
                                ))}
                            </Select>
                        </FilterGroup>
                    )}
                    <FilterGroup>
                        <FilterLabel>Tipo de Proyecto</FilterLabel>
                        <Select
                            value={tipoProyectoFilter}
                            onChange={(e) => setTipoProyectoFilter(e.target.value)}
                        >
                            <option value="">Todos</option>
                            <option value="Proyecto interno">Proyecto interno</option>
                            <option value="Producto">Producto</option>
                            <option value="Servicio">Servicio</option>
                            <option value="Consultoria">Consultoría</option>
                            <option value="I+D+i">I+D+i</option>
                            <option value="PoC">PoC</option>
                            <option value="Otros">Otros</option>
                        </Select>
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>&nbsp;</FilterLabel>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.85rem', padding: '8px 0' }}>
                            <input
                                type="checkbox"
                                checked={excludeLostCancelled}
                                onChange={(e) => setExcludeLostCancelled(e.target.checked)}
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                            Ocultar perdidas/canceladas
                        </label>
                    </FilterGroup>
                </FiltersGrid>
            </FiltersCard>

            <SummaryCard>
                <SummaryItem>
                    <SummaryValue>{totalOportunidades}</SummaryValue>
                    <SummaryLabel>Oportunidades</SummaryLabel>
                </SummaryItem>
                <SummaryItem>
                    <SummaryValue>{totalWithTeam}</SummaryValue>
                    <SummaryLabel>Con equipo</SummaryLabel>
                </SummaryItem>
                {isAdmin && (
                    <SummaryItem>
                        <SummaryValue>{formatAmount(totalAmount)}</SummaryValue>
                        <SummaryLabel>Importe total</SummaryLabel>
                    </SummaryItem>
                )}
                {isAdmin && (
                    <SummaryItem>
                        <SummaryValue>{avgProbability}%</SummaryValue>
                        <SummaryLabel>Prob. media</SummaryLabel>
                    </SummaryItem>
                )}
            </SummaryCard>

            {filteredOpportunities.length === 0 ? (
                <NoData>
                    <p>No hay oportunidades con los filtros actuales</p>
                </NoData>
            ) : (
                <>
                    <TableContainer>
                        <StyledTable>
                            <TableHeader>
                                <tr>
                                    <HeaderCell>Oportunidad</HeaderCell>
                                    <HeaderCell>Estado</HeaderCell>
                                    <HeaderCell>Sector / Tech</HeaderCell>
                                    {isAdmin && <HeaderCell>Importe</HeaderCell>}
                                    {isAdmin && <HeaderCell>Prob.</HeaderCell>}
                                    <HeaderCell>Cód. Oferta</HeaderCell>
                                    <HeaderCell>📋 Oferta (Fechas)</HeaderCell>
                                    <HeaderCell>📋 Equipo Oferta</HeaderCell>
                                    <HeaderCell>Cód. Proyecto</HeaderCell>
                                    <HeaderCell>🚀 Proyecto (Fechas)</HeaderCell>
                                    <HeaderCell>🚀 Equipo Proyecto</HeaderCell>
                                </tr>
                            </TableHeader>
                            <TableBody>
                                {filteredOpportunities.map((opp) => (
                                    <TableRow key={opp.id} onClick={() => handleRowClick(opp.id)}>
                                        <TableCell>
                                            <OpportunityName
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    if (isAdmin) {
                                                        handleDownloadOffer(e, opp.id, opp.name)
                                                    } else {
                                                        setSummaryModalOppId(parseInt(opp.id))
                                                    }
                                                }}
                                                title={isAdmin ? "📥 Descargar documento de oferta" : "📄 Ver resumen de la oferta"}
                                            >
                                                {opp.name}
                                            </OpportunityName>
                                            <OpportunityClient>🏢 {opp.client}</OpportunityClient>
                                        </TableCell>
                                        <TableCell>
                                            <StatusBadge status={opp.status}>{formatStatusLabel(opp.status)}</StatusBadge>
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                                {opp.sector && <CategoryBadge>📂 {opp.sector}</CategoryBadge>}
                                                {opp.technologies && opp.technologies.length > 0 ? (
                                                    opp.technologies.map((tech, idx) => (
                                                        <CategoryBadge key={idx}>💻 {tech}</CategoryBadge>
                                                    ))
                                                ) : opp.technology ? (
                                                    <CategoryBadge>💻 {opp.technology}</CategoryBadge>
                                                ) : null}
                                                {!opp.sector && (!opp.technologies || opp.technologies.length === 0) && !opp.technology && <span style={{ color: '#999' }}>-</span>}
                                            </div>
                                        </TableCell>
                                        {isAdmin && (
                                            <TableCell>
                                                <AmountCell>
                                                    {formatAmount(opp.amount)}
                                                </AmountCell>
                                            </TableCell>
                                        )}
                                        {isAdmin && (
                                            <TableCell>
                                                {opp.probability !== undefined && opp.probability !== null ? (
                                                    <ProbabilityCell value={probabilityToPercent(opp.probability)}>
                                                        {formatProbability(opp.probability)}
                                                    </ProbabilityCell>
                                                ) : (
                                                    <span style={{ color: '#999' }}>-</span>
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                                {opp.oferta_code || <span style={{ color: '#999' }}>-</span>}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <DateCell>
                                                <div>
                                                    <DateLabel>Inicio: </DateLabel>
                                                    <DateValue>{formatDate(opp.oferta.fecha_inicio)}</DateValue>
                                                </div>
                                                <div>
                                                    <DateLabel>Entrega: </DateLabel>
                                                    <DateValue>{formatDate(opp.oferta.fecha_entrega)}</DateValue>
                                                </div>
                                            </DateCell>
                                        </TableCell>
                                        <TableCell>
                                            {opp.oferta.equipo && opp.oferta.equipo.length > 0 ? (
                                                <TeamList>
                                                    {opp.oferta.equipo.map((member, idx) => (
                                                        <TeamMember key={idx} load={isAdmin ? member.load : 0}>
                                                            {member.userName}
                                                            {isAdmin && <LoadBadge load={member.load}>({member.load}%)</LoadBadge>}
                                                        </TeamMember>
                                                    ))}
                                                </TeamList>
                                            ) : (
                                                <NoTeam>Sin equipo</NoTeam>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                                {opp.proyecto.codigo || <span style={{ color: '#999' }}>-</span>}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <DateCell>
                                                <div>
                                                    <DateLabel>Inicio: </DateLabel>
                                                    <DateValue>{formatDate(opp.proyecto.fecha_inicio)}</DateValue>
                                                </div>
                                                <div>
                                                    <DateLabel>Fin: </DateLabel>
                                                    <DateValue>{formatDate(opp.proyecto.fecha_fin)}</DateValue>
                                                </div>
                                            </DateCell>
                                        </TableCell>
                                        <TableCell>
                                            {opp.proyecto.equipo && opp.proyecto.equipo.length > 0 ? (
                                                <TeamList>
                                                    {opp.proyecto.equipo.map((member, idx) => (
                                                        <TeamMember key={idx} load={isAdmin ? member.load : 0}>
                                                            {member.userName}
                                                            {isAdmin && <LoadBadge load={member.load}>({member.load}%)</LoadBadge>}
                                                        </TeamMember>
                                                    ))}
                                                </TeamList>
                                            ) : (
                                                <NoTeam>Sin equipo</NoTeam>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </StyledTable>
                    </TableContainer>

                    <Legend>
                        <LegendItem>
                            <LegendColor color="#2E7D32" />
                            <span>Ganada</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#1565C0" />
                            <span>En Curso</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#7B1FA2" />
                            <span>Presentada / Prospecto</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#FF9800" />
                            <span>En Negociación / Pendiente</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#C62828" />
                            <span>Perdida / Cancelada</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#0288D1" />
                            <span>En Preparación / En Revisión</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#757575" />
                            <span>Borrador</span>
                        </LegendItem>
                    </Legend>
                </>
            )}

            {summaryModalOppId && (
                <OpportunitySummaryModal
                    opportunityId={summaryModalOppId}
                    onClose={() => setSummaryModalOppId(null)}
                    isAdmin={isAdmin}
                />
            )}
        </PageContainer>
    )
}
